import { FlexBox, Tooltip } from '@dixa/lib-shared-habla';
import isPlainObject from 'lodash/isPlainObject';
import { ReactNode } from 'react';

import {
  createPrettyKeyCombination,
  KeyboardKeyCombination as KeyboardKeyCombinationT,
  PrettyKeyCombinationOptions,
  useCodeKeyMap,
} from 'utils/keyboard';

import { KeyboardKey } from '../keyboardKey/keyboardKey';

import styles from './keyboardKeyCombination.module.scss';

type KeyboardKeyCombinationProps = {
  /** The key combination. */
  keyCombination: KeyboardKeyCombinationT;
  /** The variant of displayed key combination. */
  variant?: KeyboardKeyCombinationVariant;
};

/** Represents a component for displaying a single keyboard key combination. */
export const KeyboardKeyCombination = (
  props: KeyboardKeyCombinationProps,
): JSX.Element => {
  const { keyCombination, variant = 'plain' } = props;
  const { wrapperFn = (content) => content, prettyKeyCombinationOptions } =
    isCustomVariant(variant)
      ? variant
      : keyboardKeyCombinationVariantConfig[variant];
  const codeKeyMap = useCodeKeyMap();

  const content = createPrettyKeyCombination(keyCombination, {
    ...prettyKeyCombinationOptions,
    codeKeyMap,
  });

  return <>{wrapperFn(content)}</>;
};

/** Represents the available keyboard key combination named variants. */
export type KeyboardKeyCombinationNamedVariant =
  keyof typeof keyboardKeyCombinationVariantConfig;

/** Represents the available keyboard key combination variants. */
export type KeyboardKeyCombinationVariant =
  | KeyboardKeyCombinationNamedVariant
  | KeyboardKeyCombinationVariantConfig;

/** Represents the keyboard key combinations variant configuration. */
export type KeyboardKeyCombinationVariantConfig = {
  /** The pretty key combination options. */
  readonly prettyKeyCombinationOptions: Omit<
    PrettyKeyCombinationOptions,
    'codeKeyMap'
  >;
  /** The function for wrapping the final output. */
  readonly wrapperFn?: (content: ReactNode) => ReactNode;
};

/** Represents the variants configuration. */
export const keyboardKeyCombinationVariantConfig = {
  plain: {
    prettyKeyCombinationOptions: {
      keySeparator: '+',
      keyTransformFn: (key) => (
        <kbd key={key} className={styles.kbd}>
          {key}
        </kbd>
      ),
    },
    wrapperFn: (content) => <span>{content}</span>,
  },
  simple: {
    prettyKeyCombinationOptions: {
      keySeparator: '',
      keyTransformFn: (key) => (
        <KeyboardKey key={key} variant="simple">
          {key}
        </KeyboardKey>
      ),
    },
    wrapperFn: (content) => (
      <FlexBox gap="100" alignItems="center">
        {content}
      </FlexBox>
    ),
  },
  rich: {
    prettyKeyCombinationOptions: {
      keySeparator: '',
      keyTransformFn: (key, attributes) => (
        <Tooltip
          key={key}
          disabled={!attributes?.description}
          content={attributes?.description}
          dark
        >
          <KeyboardKey variant="rich">{key}</KeyboardKey>
        </Tooltip>
      ),
    },
    wrapperFn: (content) => (
      <FlexBox gap="200" alignItems="center">
        {content}
      </FlexBox>
    ),
  },
} as const satisfies Record<string, KeyboardKeyCombinationVariantConfig>;

/** A type guard for determining whether the variant is a custom configuration. */
function isCustomVariant(
  variant: KeyboardKeyCombinationVariant,
): variant is KeyboardKeyCombinationVariantConfig {
  return isPlainObject(variant);
}
